import request from "@/views/setting/my-practice/service/requestV1.js"
import requestV1 from "@/views/setting/my-practice/service/requestV1.js"
import { ref } from "vue"
import { INLINE_EMAIL_ERRORS } from "./useEmailDomainCheck"

const get2faURL = "setting/getUserInformation"
const turnOff2faURL = "setting/turnOff2FASettingPage"

const isLoading = ref(true);
const hasError = ref(false);
const on2fa = ref()
const primary_email = ref()
const userName = ref()
const email2fa = ref()
const phone2fa = ref()
const email2fa_id = ref()
const phone2fa_id = ref()

// get2faData's isLoading guard only goes false once the first response lands, so
// callers that mount together (the Login Email row and the 2FA row on Account
// Info) each saw "still loading" and each fired their own getUserInformation.
// They share this one in-flight request instead.
let pendingFetch = null

export default function use2FAApi() {
    const fetchOnfromAPI = async () => {
        isLoading.value = true;
        hasError.value = false;
        return request.get(get2faURL).then(res => {
            on2fa.value = res.data.result.isTwoFactorAuthenticationEnabled;
            primary_email.value = res.data.result.email_user_id;
            userName.value = res.data.result.username;
            if (on2fa.value) {
                return fetch2faInfoFromAPI()
            } else {
                isLoading.value = false;
                hasError.value = false;
                email2fa.value = ""
                phone2fa.value = ""
            }
        }).catch((err) => {
            isLoading.value = false;
            hasError.value = true;
            console.log(err);
        })
    }

    const fetch2faInfoFromAPI = async (params) => {
        return requestV1.get('valogin/getUser2FAContact').then(res => {
            isLoading.value = false;
            hasError.value = false;
            return getContact(res.data)
        }).catch((err) => {
            isLoading.value = false;
            hasError.value = true;
            console.log(err);
        })
    }

    const getContact = (contacts) => {
        if (contacts?.length > 0) {
            contacts.forEach(contact => {
                if (contact.contact_description === "email_2fa") {
                    email2fa.value = contact.contact_details;
                    email2fa_id.value = contact.contact_id;
                }
                else if (contact.contact_description === "phone_2fa") {
                    phone2fa.value = contact.contact_details;
                    phone2fa_id.value = contact.contact_id;
                }
            })
        }
    }

    const get2faData = async () => {
        if (isLoading.value && !hasError.value) {
            if (!pendingFetch) pendingFetch = fetchOnfromAPI().finally(() => { pendingFetch = null });
            await pendingFetch;
        }
        return on2fa.value;
    }

    const sendVerification = async (payload) => {
        return requestV1.post('setting/sendVerify2FASetUpContactInfo', payload).then(res => {
            return res?.data?.code
        }).catch((error) => {
            console.log(error)
            return 400;
        })
    }

    const sendVerificationToSetupEmail = async (email) => {
        return sendVerification({
            username: userName.value,
            email_address: email,
            phone_number: null
        })
    }

    const sendVerificationToSetupPhone = async (phone) => {
        return sendVerification({
            username: userName.value,
            email_address: null,
            phone_number: phone
        })
    }

    const sendVerificationTo2fa = async (payload) => {
        return requestV1.post("setting/send2FAVerificationCode", payload).then(res => {
            return res?.data?.code
        }).catch((error) => {
            console.log(error)
            return 400;
        })
    }

    const sendVerificationTo2faEmail = async () => {
        return sendVerificationTo2fa({
            username: userName.value,
            email_address: email2fa.value,
            phone_number: null
        })
    }

    const sendVerificationTo2faPhone = async () => {
        return sendVerificationTo2fa({
            username: userName.value,
            email_address: null,
            phone_number: phone2fa.value
        })
    }

    const verifyToSetupEmail = async (email, code) => {
        return requestV1.post("setting/verify2FASetUpContactInfo", {
            username: userName.value,
            verification_code: code,
            email_address: email,
            phone_number: null
        }).then(res => {
            if (res?.data?.code === 200) email2fa.value = email
            return res?.data?.code
        }).catch((error) => {
            return 400
        })
    }

    const verify2faCode = async (payload) => {
        return requestV1.post("setting/verify2FAVerificationCode", payload).then(res => {
            return res.data
        }).catch(() => {
            return {code:400}
        })
    }

    const verify2faEmailCode = async (code) => {
        return verify2faCode({
            username: userName.value,
            verification_code: code,
            email_address: email2fa.value,
            phone_number: null
        })
    }

    const verify2faPhoneCode = async (code) => {
        return verify2faCode({
            username: userName.value,
            verification_code: code,
            email_address: null,
            phone_number: phone2fa.value
        })
    }

    const turnOn2fa = async (payload) => {
        return requestV1.put("setting/turnOn2FASettingPage", payload).then(res => {
            if (res?.data?.code === 200) {
                on2fa.value = true;
                phone2fa.value = payload.phone_number;
            }
            else { phone2fa.value = ""; email2fa.value = "" }
            return res?.data?.code
        }).catch((error) => {
            return 400
        })
    }

    const turnOff2fa = async (payload) => {
        return requestV1.put(turnOff2faURL, payload).then(res => {
            if (res?.data?.code === 200) on2fa.value = false;
            return res?.data?.code
        }).catch((error) => {
            return 400
        })
    }

    const turnOff2faWithEmail = async (code) => {
        return turnOff2fa({
            username: userName.value,
            email_address: email2fa.value,
            phone_number: null,
            verification_code: code
        })
    }

    const turnOff2faWithPhone = async (code) => {
        return turnOff2fa({
            username: userName.value,
            email_address: null,
            phone_number: phone2fa.value,
            verification_code: code
        })
    }

    const update2fa = async (payload) => {
        return requestV1.put("setting/updateUser2FAContact", payload).then(res => {
            return res?.data?.code
        }).catch((error) => {
            return 400
        })
    }

    const update2faEmail = async (email, code) => {
        if(!email2fa_id.value) await fetch2faInfoFromAPI();
        let res = await update2fa({
            username: userName.value,
            verification_code: code,
            new_email_address: email,
            old_email_address: email2fa.value,
            old_email_address_contact_id: email2fa_id.value,
            new_phone_number: null,
            old_phone_number_contact_id: null,
            old_phone_number: null
        })
        if (res === 200) {
            email2fa.value = email
            return 200
        } else {
            return 400
        }
    }

    const update2faPhone = async (phone, code) => {
        if(!phone2fa_id.value) await fetch2faInfoFromAPI();
        let res = await update2fa({
            username: userName.value,
            verification_code: code,
            new_email_address: null,
            old_email_address: null,
            old_email_address_contact_id: null,
            new_phone_number: phone,
            old_phone_number_contact_id: phone2fa_id.value,
            old_phone_number: phone2fa.value
        })
        if (res === 200) {
            phone2fa.value = phone
            return 200
        } else {
            return 400
        }
    }

    const changeLoginEmail =async (payload)=>{
        return requestV1.post('setting/changeUserEmailLogInID',payload,INLINE_EMAIL_ERRORS).then(res=>{
            if(res.data?.message==="Email log in ID Updated") primary_email.value=payload.new_email_user_id;
            return res.data
        }).catch((err)=>{
            // keep the 400 body: an email rejection carries error_code / message / suggestion (VP-18719)
            return {...(err?.response?.data || {}), code:400}
        })
    }

    const changePassword=async (payload)=>{
        return requestV1.post('setting/changePassword',payload).then(res=>{
            if(res.data?.message==="Email log in ID Updated.") primary_email.value=payload.new_email_user_id;
            return res.data
        }).catch(()=>{
            return {code:400}
        })
    }     


    return { get2faData, sendVerificationToSetupEmail, sendVerificationToSetupPhone, sendVerificationTo2faEmail, sendVerificationTo2faPhone, verifyToSetupEmail, turnOn2fa, turnOff2faWithEmail, turnOff2faWithPhone, verify2faEmailCode, verify2faPhoneCode, update2faEmail, update2faPhone,changeLoginEmail,changePassword, email2fa, phone2fa, primary_email, isLoading, hasError }

}